import {
    AccountApplication,
    CreateAccountApplicationInput,
} from '../types';

import {
    useAccountApplicationQuery,
    useCreateAccountApplicationMutation,
    useDeleteAccountApplicationMutation,
    useUpdateAccountApplicationMutation,
} from './useAccountApplicationsQuery';

// FNP-946: the single record comes from React Query. Return shape unchanged so
// the pages and forms consuming this hook did not change.
export const useAccountApplication = (
    accountId: string,
    accountApplicationId?: string,
) => {

    // Preserved from the Redux version: callers must supply an account context.
    // The record is fetched by its own id, but an absent accountId means the
    // caller has not resolved the user account yet, which is a caller bug.
    if (!accountId) {
        throw new Error('accountId is required parameter');
    }

    const {
        data: record,
        isLoading,
        refetch
    } = useAccountApplicationQuery(accountApplicationId);

    const createMutation = useCreateAccountApplicationMutation();
    const updateMutation = useUpdateAccountApplicationMutation();
    const deleteMutation = useDeleteAccountApplicationMutation();

    // FETCH
    const fetchRecord = () => {
        if (typeof accountApplicationId === 'undefined') {
            throw new Error('accountApplicationId is required parameter');
        }
        refetch();
    };

    // CREATE, UPDATE, DELETE
    const createRecord = (values: CreateAccountApplicationInput) => {
        createMutation.mutate(values as unknown as AccountApplication);
    };

    const updateRecord = (values: AccountApplication) => {
        if (typeof accountApplicationId === 'undefined') {
            throw new Error('accountApplicationId is required parameter');
        }

        updateMutation.mutate({
            id: accountApplicationId,
            record: values,
        });
    };

    const deleteRecord = () => {
        if (typeof accountApplicationId === 'undefined') {
            throw new Error('accountApplicationId is required parameter');
        }
        deleteMutation.mutate(accountApplicationId);
    };

    return {
        record,
        loading: isLoading,
        fetchRecord,
        createRecord,
        updateRecord,
        deleteRecord,
    };
};
